
import FileResultCard from './FileResultCard.vue';
import GpsChip from './GpsChip.vue';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { IDocumentMetadata, IFileGpsData, IMetadataAttachment, IMetadataImage, ITimeBasedMetadata, IDetectedFaceEntry, ILivePhotoInfoEntry, MediaType } from '@/types/resource.types';
import fileService from '@/services/ProjectFileService';

@Component({
    name: 'FileMetadata',
    components: {
        FileResultCard,
        GpsChip
    }
})
export default class FileMetadata extends Vue {

    /* Props
    ============================================*/

    @Prop({type: Object, required: true})
    readonly metadata: Record<string, string>;
    @Prop({type: Object, required: true})
    readonly gps: IFileGpsData;
    @Prop({type: Object, default: () => ({ location: [], slte: [], sensor: [], detectedFaces: [], livePhotoInfo: [], transcription: [], audioTags: [], waveform: [] })})
    readonly timeBasedMetadata: ITimeBasedMetadata;
    @Prop({type: Number, default: MediaType.Video})
    readonly mediaType: MediaType;
    @Prop({type: Object, default: null})
    readonly documentMetadata: IDocumentMetadata | null;

    @Prop({type: Number, default: null})
    readonly projectFileId: number | null;

    /* Data
    ============================================*/

    activeTab: number = 0;
    hasSections: boolean = false;
    openSections: number[] = [];
    imageUrls: Record<number, string> = {};
    imageLoading: Record<number, boolean> = {};
    attachmentUrls: Record<number, string> = {};
    attachmentLoading: Record<number, boolean> = {};

    /* Computed
    ============================================*/

    get groupedValues(): { header: string; items: FileMetadataItem[] }[] {
        const groups: { header: string; items: FileMetadataItem[] }[] = [];
        let current: { header: string; items: FileMetadataItem[] } | null = null;
        for (const item of this.values) {
            if (item.value === 'HEADER') {
                current = { header: item.name, items: [] };
                groups.push(current);
            } else {
                if (!current) {
                    current = { header: '', items: [] };
                    groups.push(current);
                }
                current.items.push(item);
            }
        }
        return groups;
    }

    created() {
        this.$watch(() => this.activeDecodedGroups, (groups) => {
            this.openSections = groups.map((_, i) => i);
        }, { immediate: true });
    }

    get values(): FileMetadataItem[] {
        let self = this;
        if(!self.metadata) {};
        let data: FileMetadataItem[] = [];
        Object.keys(self.metadata).forEach(key => {
            let index = parseInt(key.split(':')[0]);
            if(!isNaN(index)) {
                let values = key.split(': ');
                let numValues = values.length;
                if(numValues == 3){
                    this.hasSections = true;
                    data[index - 1] = {
                        name: key.split(': ')[2],
                        value: self.metadata[key],
                        section: key.split(': ')[1]
                    };
                }
                else{
                    data[index - 1] = {
                        name: key.split(': ')[1],
                        value: self.metadata[key]
                    };
                }
            }
        });
        return data;
    }

    get isDocument(): boolean {
        return this.mediaType === MediaType.Document;
    }

    get hasImages(): boolean {
        return !!(this.documentMetadata?.images?.length);
    }

    get hasFonts(): boolean {
        return !!(this.documentMetadata?.fonts?.length);
    }

    get hasAttachments(): boolean {
        return !!(this.documentMetadata?.attachments?.length);
    }

    get documentDecodedGroups(): { header: string; items: FileMetadataItem[] }[] {
        if (!this.documentMetadata?.metadata?.length) return [];
        const groupMap = new Map<string, FileMetadataItem[]>();
        for (const row of this.documentMetadata.metadata) {
            const section = row.section ?? '';
            if (!groupMap.has(section)) groupMap.set(section, []);
            groupMap.get(section)!.push({ name: row.displayName, value: row.value });
        }
        return Array.from(groupMap.entries()).map(([header, items]) => ({ header, items }));
    }

    get activeDecodedGroups(): { header: string; items: FileMetadataItem[] }[] {
        return this.isDocument ? this.documentDecodedGroups : this.groupedValues;
    }

    get hasTimeBasedMetadata(): boolean {
        return this.hasLocation || this.hasSlte || this.hasSensor || this.hasDetectedFaces || this.hasLivePhotoInfo || this.hasTranscription || this.hasAudioTags || this.hasWaveform;
    }

    get hasLocation(): boolean {
        return !!(this.timeBasedMetadata?.location?.length);
    }

    get hasSlte(): boolean {
        return !!(this.timeBasedMetadata?.slte?.length);
    }

    get hasSensor(): boolean {
        return !!(this.timeBasedMetadata?.sensor?.length);
    }

    get hasDetectedFaces(): boolean {
        return !!(this.timeBasedMetadata?.detectedFaces?.length);
    }

    get hasLivePhotoInfo(): boolean {
        return !!(this.timeBasedMetadata?.livePhotoInfo?.length);
    }

    get hasTranscription(): boolean {
        return !!(this.timeBasedMetadata?.transcription?.length);
    }

    get hasAudioTags(): boolean {
        return !!(this.timeBasedMetadata?.audioTags?.length);
    }

    get hasWaveform(): boolean {
        return !!(this.timeBasedMetadata?.waveform?.length);
    }

    get locationCsvRows(): (string | undefined)[][] {
        const headers = ['Timestamp', 'Framing', 'Sequence', 'Latitude', 'Longitude', 'Altitude', 'Speed', 'HDOP'];
        const rows = (this.timeBasedMetadata?.location ?? []).map(e => [
            e.timestamp, e.framing, e.sequence, e.latitude, e.longitude, e.altitude, e.speed, e.hdop
        ]);
        return [headers, ...rows];
    }

    get slteCsvRows(): (string | undefined)[][] {
        const headers = ['Timestamp', 'Frame Counter', 'Session Hash', 'Record Index', 'Device Serial', 'Status Flag'];
        const rows = (this.timeBasedMetadata?.slte ?? []).map(e => [
            e.timestamp, e.frameCounter, e.sessionHash, e.recordIndex, e.deviceSerial, e.statusFlag
        ]);
        return [headers, ...rows];
    }

    get sensorCsvRows(): (string | undefined)[][] {
        const headers = ['Sensor Type', 'Counter', 'File Offset', 'Sample Block'];
        const rows = (this.timeBasedMetadata?.sensor ?? []).map(e => [
            e.sensorType, e.counter, e.fileOffset, e.sampleBlock
        ]);
        return [headers, ...rows];
    }

    get detectedFacesCsvRows(): (string | undefined)[][] {
        const headers = ['File Offset', 'Face ID', 'Top Left', 'Top Right', 'Bottom Right', 'Bottom Left', 'Roll Angle', 'Yaw Angle'];
        const rows = (this.timeBasedMetadata?.detectedFaces ?? []).map((e: IDetectedFaceEntry) => [
            e.fileOffset, e.faceId, e.boundsTL, e.boundsTR, e.boundsBR, e.boundsBL, e.rollAngle, e.yawAngle
        ]);
        return [headers, ...rows];
    }

    get livePhotoInfoCsvRows(): (string | undefined)[][] {
        const headers = ['File Offset', 'Sample Size', 'Version', 'Exposure Time', 'Avg Focus Score', 'ISP Motion', 'ISP Scale', 'Lens Position', 'AF Status', 'Focusing Method', 'Field Validity Mask', 'Face Count', 'Face Timestamp', 'Face Bounds', 'Face ID', 'Face Roll/Yaw', 'Face Flags', 'Stream Face Counts', 'Sec Face Timestamp', 'Sec Face Bounds', 'Sec Face ID', 'Sec Face Roll/Yaw', 'Sec Face Flags', 'V3 Validity Flags', 'Stab Matrix', 'CMTime Host 1', 'CMTime Host 2', 'Iris Recipe', 'Iris Displacement', 'Interpolated Frame'];
        const rows = (this.timeBasedMetadata?.livePhotoInfo ?? []).map((e: any) => [
            e.fileOffset, e.sampleSize, e.version, e.exposureTime, e.avgFocusScore, e.ispMotion, e.ispScale, e.lensPosition, e.afStatus, e.focusingMethod, e.fieldValidityMask, e.faceCount, e.faceTimestamp, e.faceBounds, e.faceId, e.faceRollYaw, e.faceFlags, e.streamFaceCounts, e.secFaceTimestamp, e.secFaceBounds, e.secFaceId, e.secFaceRollYaw, e.secFaceFlags, e.v3ValidityFlags, e.stabMatrix, e.cmTimeHost1, e.cmTimeHost2, e.irisRecipe, e.irisDisplacement, e.interpolatedFrame
        ]);
        return [headers, ...rows];
    }

    get transcriptionCsvRows(): (string | undefined)[][] {
        const headers = ['Track ID', 'Sample #', 'File Offset', 'Raw Text', 'Formatted Text', 'Start (ms)', 'End (ms)', 'Confidence Level', 'Confidence Score', 'Overall Confidence', 'Locale'];
        const rows = (this.timeBasedMetadata?.transcription ?? []).map(entry => [
            entry.trackId, String(entry.sampleNumber), entry.fileOffset, entry.rawText, entry.formattedText, entry.startTimeMs,
            entry.endTimeMs, entry.confidenceLevel, entry.confidenceScore, entry.overallConfidence, entry.locale
        ]);
        return [headers, ...rows];
    }

    get audioTagsCsvRows(): (string | undefined)[][] {
        const headers = ['Track ID', 'Sample #', 'File Offset', 'Timestamp (ms)', 'Tag Type', 'Confidence'];
        const rows = (this.timeBasedMetadata?.audioTags ?? []).map(entry => [
            entry.trackId, String(entry.sampleNumber), entry.fileOffset, entry.timestampMs, entry.tagType, entry.confidence
        ]);
        return [headers, ...rows];
    }

    get waveformCsvRows(): (string | undefined)[][] {
        const headers = ['Track ID', 'Sample #', 'File Offset', 'Timestamp (ms)', 'Amplitude'];
        const rows = (this.timeBasedMetadata?.waveform ?? []).map(entry => [
            entry.trackId, String(entry.sampleNumber), entry.fileOffset, entry.timestampMs, entry.amplitude
        ]);
        return [headers, ...rows];
    }

    /* Methods
    ============================================*/

    private async fetchImageUrl(img: IMetadataImage): Promise<string | null> {
        if (this.imageUrls[img.id]) return this.imageUrls[img.id];
        if (!this.projectFileId) return null;
        this.$set(this.imageLoading, img.id, true);
        try {
            const url = await fileService.getPdfImageUrl(this.projectFileId, img.id);
            this.$set(this.imageUrls, img.id, url);
            return url;
        } catch {
            return null;
        } finally {
            this.$set(this.imageLoading, img.id, false);
        }
    }

    async viewImage(img: IMetadataImage): Promise<void> {
        const url = await this.fetchImageUrl(img);
        if (url) window.open(url, '_blank');
    }

    async downloadImage(img: IMetadataImage): Promise<void> {
        const url = await this.fetchImageUrl(img);
        if (!url) return;
        const ext = img.declaredFormat === 'jpeg' ? 'jpg' : (img.declaredFormat ?? 'dat');
        const filename = `image_${img.objectNumber > 0 ? img.objectNumber : img.id}.${ext}`;
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
    }

    async downloadAttachment(att: IMetadataAttachment): Promise<void> {
        if (!this.projectFileId) return;
        this.$set(this.attachmentLoading, att.id, true);
        try {
            const blob = await fileService.downloadPdfAttachment(this.projectFileId, att.id);
            const blobUrl = URL.createObjectURL(blob);
            const filename = att.nameUF ?? att.nameF ?? `attachment_${att.id}`;
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch {
            // silent
        } finally {
            this.$set(this.attachmentLoading, att.id, false);
        }
    }

    private triggerDownload(url: string, filename: string): void {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
    }

    downloadCsv(rows: (string | undefined)[][], filename: string): void {
        const csv = rows.map(row =>
            row.map(v => {
                const s = (v ?? '').toString();
                const safe = /^[\u0000- ]*[=+\-@]/.test(s) ? `'${s}` : s;
                return safe.includes(',') || safe.includes('"') || safe.includes('\n')
                    ? `"${safe.replace(/"/g, '""')}"`
                    : safe;
            }).join(',')
        ).join('\r\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    }

}

type FileMetadataItem = {
    name: string;
    value: string;
    section?: string;
}

